/**
 * Renewals — our engine's charges at a glance.
 *
 * The AR Report describes SimpleCirc's billing. Once a title is cut over,
 * its renewals are ours: this page is where a billing run is watched.
 * Top: what we charged lately. Middle: every member whose renewal is
 * failing right now, soonest-to-lapse first, with when we try again and
 * how long they keep reading — or that we are waiting for a new card
 * after a hard decline. Bottom: the attempts themselves.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { PubBadge } from '../components/PubBadge'
import { fmtInt, fmtMoney } from '../lib/format'
import { ExpiryNotices } from './ExpiryNotices'
import { RenewalEmailResults } from './RenewalEmailResults'

type Totals = {
  charged: number
  failed: number
  new_card_retries: number
  ended_no_new_card: number
  in_retry: number
  waiting_for_new_card: number
}

type InRetry = {
  subscription_id: number
  subscriber_id: number
  member_name: string
  email: string | null
  publication_slug: string
  billing_plan: string | null
  period_end: string
  attempts: number
  last_decline_code: string | null
  card_last4: string | null
  next_retry_on: string | null
  access_until: string
  waiting_for_new_card: boolean
}

type Recent = {
  attempt_id: number
  subscription_id: number
  subscriber_id: number
  publication_slug: string
  attempt_no: number
  status: string
  amount_cents: number
  decline_code: string | null
  triggered_by: string
  made_on: string
}

type Overview = {
  as_of: string
  window_days: number
  totals: Totals
  in_retry: InRetry[]
  recent: Recent[]
}

const WINDOWS = [7, 14, 30, 90]

function triggeredByLabel(t: string): string {
  if (t === 'scheduler') return 'schedule'
  if (t === 'card_changed') return 'new card'
  if (t === 'hard_decline_close') return 'no new card — ended'
  if (t.startsWith('admin:')) return 'charge now'
  return t
}

export function RenewalsPage() {
  const [days, setDays] = useState(14)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['renewals-overview', days],
    queryFn: () => api.get<Overview>(`/api/renewals/overview?days=${days}`),
    refetchInterval: 60_000,
  })
  const t = data?.totals

  return (
    <div className="p-8 space-y-6 max-w-[1500px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Members · our billing</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
          Renewals
          <span className="text-amber-glow"> ∙ </span>
          <span className="text-paper-trace">{fmtInt(t?.in_retry)} retrying</span>
        </h1>
        <p className="mt-2 text-sm text-paper-dim max-w-[70ch]">
          Charges our renewal engine made for titles cut over from SimpleCirc (SimpleCirc&rsquo;s own
          billing is in the AR Report). A failing renewal is retried on its plan&rsquo;s schedule
          (Settings → Payments) and the member keeps reading until the last retry. After a lost,
          stolen, closed or expired card we stop retrying that card and wait for a new one. A card
          the member adds is charged within 15 minutes; one Authorize.Net&rsquo;s Account Updater
          replaces is picked up by the next morning&rsquo;s check.
        </p>
      </header>

      <div className="flex items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="label">Window</span>
          <select
            id="renewals-window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-ink-900 border border-ink-500 px-2 py-1 font-mono text-[12px] text-paper"
          >
            {WINDOWS.map((w) => (
              <option key={w} value={w}>Last {w} days</option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && <p className="label">Loading…</p>}
      {isError && <p className="text-radar-red font-mono text-[12px]">Couldn&rsquo;t load renewals.</p>}

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-ink-500 border border-ink-500">
          <Stat label="Charged" value={t.charged} tone="green" />
          <Stat label="Failed attempts" value={t.failed} tone="red" />
          <Stat label="New-card retries" value={t.new_card_retries} />
          <Stat label="Ended, no new card" value={t.ended_no_new_card} />
          <Stat label="Retrying now" value={t.in_retry} tone="amber" />
          <Stat label="Waiting for a new card" value={t.waiting_for_new_card} tone="amber" />
        </div>
      )}

      {data && (
        <section className="space-y-2">
          <h2 className="label">Retrying now · soonest to lapse first</h2>
          {data.in_retry.length === 0 ? (
            <p className="text-sm text-paper-dim">No renewal is failing right now.</p>
          ) : (
            <div className="border border-ink-500 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ink-500">
                    <Th>Member</Th>
                    <Th>Title</Th>
                    <Th>Plan</Th>
                    <Th>Due</Th>
                    <Th>Tries</Th>
                    <Th>Last decline</Th>
                    <Th>Next</Th>
                    <Th>Reads until</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.in_retry.map((r) => (
                    <tr key={r.subscription_id} className="border-b border-ink-700/40 hover:bg-ink-800/40">
                      <Td>
                        <Link to={`/subscribers/${r.subscriber_id}`} className="text-paper hover:text-amber-glow hover:underline">
                          {r.member_name || r.email || `#${r.subscriber_id}`}
                        </Link>
                        {r.email && r.member_name && (
                          <div className="text-paper-trace text-[11px] font-mono">{r.email}</div>
                        )}
                      </Td>
                      <Td><PubBadge slug={r.publication_slug} /></Td>
                      <Td mono subtle>{r.billing_plan ?? 'annual'}</Td>
                      <Td mono subtle>{r.period_end}</Td>
                      <Td mono subtle>{r.attempts}</Td>
                      <Td mono subtle>{r.last_decline_code ?? '—'}</Td>
                      <Td mono>
                        {r.waiting_for_new_card ? (
                          <span className="text-radar-amber">waiting for a new card</span>
                        ) : (r.next_retry_on ?? 'in progress')}
                      </Td>
                      <Td mono>{r.access_until}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <ExpiryNotices />

      <RenewalEmailResults />

      {data && (
        <section className="space-y-2">
          <h2 className="label">Attempts · last {data.window_days} days</h2>
          {data.recent.length === 0 ? (
            <p className="text-sm text-paper-dim">
              No renewal attempts in this window. Titles still billed by SimpleCirc don&rsquo;t appear here.
            </p>
          ) : (
            <div className="border border-ink-500 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ink-500">
                    <Th>Day</Th>
                    <Th>Member</Th>
                    <Th>Title</Th>
                    <Th>Try</Th>
                    <Th>Result</Th>
                    <Th>Amount</Th>
                    <Th>Decline</Th>
                    <Th>Why now</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((a) => (
                    <tr key={a.attempt_id} className="border-b border-ink-700/40 hover:bg-ink-800/40">
                      <Td mono subtle>{a.made_on}</Td>
                      <Td>
                        <Link to={`/subscribers/${a.subscriber_id}`} className="text-paper hover:text-amber-glow hover:underline">
                          #{a.subscriber_id}
                        </Link>
                      </Td>
                      <Td><PubBadge slug={a.publication_slug} /></Td>
                      <Td mono subtle>{a.attempt_no}</Td>
                      <Td>
                        <span className={'label ' + (a.status === 'succeeded' ? 'text-radar-green'
                          : a.status === 'failed' ? 'text-radar-red' : 'text-radar-amber')}>
                          {a.status}
                        </span>
                      </Td>
                      <Td mono>{fmtMoney(a.amount_cents / 100, false, 2)}</Td>
                      <Td mono subtle>{a.decline_code ?? '—'}</Td>
                      <Td mono subtle>{triggeredByLabel(a.triggered_by)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'green' | 'red' | 'amber' }) {
  const color = tone === 'green' ? 'text-radar-green' : tone === 'red' ? 'text-radar-red'
    : tone === 'amber' ? 'text-radar-amber' : 'text-paper'
  return (
    <div className="bg-ink-900 p-4">
      <div className="label mb-1">{label}</div>
      <div className={`font-mono tabular-nums text-[26px] ${color}`}>{fmtInt(value)}</div>
    </div>
  )
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="label py-2 px-4 font-normal text-left">{children}</th>
}

function Td({ children, mono, subtle }: { children: React.ReactNode; mono?: boolean; subtle?: boolean }) {
  return (
    <td className={`py-2 px-4 text-[13px] align-top ${mono ? 'font-mono' : ''} ${subtle ? 'text-paper-dim' : 'text-paper'}`}>
      {children}
    </td>
  )
}
